import LockIcon from "@mui/icons-material/Lock";
import { toast } from '../../utils/toast';
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Turnstile from "react-turnstile";
import Swal from "sweetalert2";
import { useAuth } from "../../context/AuthContext";
import logo from "../../img/login.png";
import "./login.scss";

interface LoginFormInputs {
  Usuario: string;
  Contrasena: string;
}

interface RecaptchaConfig {
  activo: boolean;
  siteKey: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4020";

const Login: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<LoginFormInputs>({ mode: "onChange" });

  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaConfig, setRecaptchaConfig] = useState<RecaptchaConfig>({
    activo: false,
    siteKey: null,
  });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0); // forzar re-render del widget

  const navigate = useNavigate();
  const { login } = useAuth();

  // Cargar configuración de reCAPTCHA desde el backend al montar
  useEffect(() => {
    axios
      .post(`${API_URL}/configuracion-sistema/recaptcha-config`)
      .then((res) => setRecaptchaConfig(res.data))
      .catch(() => {
        // Si el endpoint falla, asume reCAPTCHA inactivo para no bloquear el login
        setRecaptchaConfig({ activo: false, siteKey: null });
      });
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    // Incrementar la key fuerza el desmontaje y remontaje del widget (nuevo challenge)
    setTurnstileKey((k) => k + 1);
  }, []);

  const preventClipboard = (e: React.ClipboardEvent, action: string) => {
    e.preventDefault();
    toast.warning(`Por seguridad, no se permite ${action} la contraseña`);
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const onSubmit = async (data: LoginFormInputs) => {
    // Si reCAPTCHA está activo y aún no se resolvió, bloquear
    if (recaptchaConfig.activo && !turnstileToken) {
      toast.warning("Completa la verificación de seguridad antes de continuar.");
      return;
    }

    try {
      const payload: any = { ...data };
      if (recaptchaConfig.activo && turnstileToken) {
        payload.TurnstileToken = turnstileToken;
      }

      const response = await axios.post(`${API_URL}/auth/login`, payload);
      login(response.data);
      await Swal.fire({
        icon: "success",
        title: "¡Bienvenido!",
        text: "Inicio de sesión exitoso",
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: "swal2-success-popup" },
      });
      navigate("/");
    } catch (error: any) {
      // Reiniciar el widget tras cada intento fallido para generar un nuevo token
      resetTurnstile();

      const status = error?.response?.status;
      if (status === 429) {
        toast.error("Has superado el límite de intentos. Espera unos minutos antes de volver a intentarlo.");
      } else {
        toast.error("Por favor verifica tu usuario y contraseña");
      }
    }
  };

  const passwordValue = watch("Contrasena");

  return (
    <div className="login-container">
      <div className="login-left">
        <h1>SISTEMA DE INVENTARIO Y FACTURACIÓN</h1>
      </div>

      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="Logo del sistema" className="auth-icon" />
          <h2 className="login-title">Iniciar Sesión</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Usuario */}
          <div className="form-group">
            <label htmlFor="usuario" className="input-label">
              Usuario
            </label>
            <div className="input-with-icon">
              <PersonIcon className="input-icon" />
              <input
                id="usuario"
                type="text"
                placeholder="Ingresa tu usuario"
                {...register("Usuario", {
                  required: "El usuario es obligatorio",
                  minLength: { value: 3, message: "Mínimo 3 caracteres" },
                })}
                className={errors.Usuario ? "input-error" : ""}
                aria-invalid={!!errors.Usuario}
                aria-describedby={errors.Usuario ? "usuario-error" : undefined}
              />
            </div>
            {errors.Usuario && (
              <p id="usuario-error" className="error-message" role="alert">
                {errors.Usuario.message}
              </p>
            )}
          </div>

          {/* Contraseña */}
          <div className="form-group">
            <label htmlFor="contrasena" className="input-label">
              Contraseña
            </label>
            <div className="input-with-icon">
              <LockIcon className="input-icon" />
              <input
                id="contrasena"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                onPaste={(e) => preventClipboard(e, "pegar")}
                onCopy={(e) => preventClipboard(e, "copiar")}
                onCut={(e) => preventClipboard(e, "cortar")}
                {...register("Contrasena", {
                  required: "La contraseña es obligatoria",
                  minLength: { value: 6, message: "Mínimo 6 caracteres" },
                })}
                className={errors.Contrasena ? "input-error" : ""}
                aria-invalid={!!errors.Contrasena}
                aria-describedby={
                  errors.Contrasena ? "contrasena-error" : undefined
                }
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </button>
            </div>
            {errors.Contrasena && (
              <p id="contrasena-error" className="error-message" role="alert">
                {errors.Contrasena.message}
              </p>
            )}
          </div>

          {/* Cloudflare Turnstile — solo se renderiza si RecaptchaActivo = true en BD */}
          {recaptchaConfig.activo && recaptchaConfig.siteKey && (
            <div className="turnstile-wrapper">
              <Turnstile
                key={turnstileKey}
                sitekey={recaptchaConfig.siteKey}
                onVerify={(token) => setTurnstileToken(token)}
                onExpire={resetTurnstile}
                onError={resetTurnstile}
                theme="light"
                language="es"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={
              isSubmitting ||
              (recaptchaConfig.activo && !turnstileToken)
            }
            className={`login-button ${isSubmitting ? "loading" : ""}`}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                <span>Iniciando sesión...</span>
              </>
            ) : (
              "Iniciar Sesión"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
